import PropTypes from 'prop-types';

export default {};

export const FIRST_COST_CODE_KEY = 'firstCostCode';

export const COLUMN_PROP_TYPE = PropTypes.shape({
  title: PropTypes.string,
  field: PropTypes.string,
  id: PropTypes.string,
  subTotal: PropTypes.bool,
});

export const CUSTOM_EXPORT_PROP_TYPE = PropTypes.shape({
  id: PropTypes.string,
  type: PropTypes.string,
  title: PropTypes.string,
  columns: PropTypes.arrayOf(COLUMN_PROP_TYPE),
  divisionId: PropTypes.string,
  formTemplateId: PropTypes.string,
  boardId: PropTypes.string,
  boardCardTemplateId: PropTypes.string,
  dateFilterFieldId: PropTypes.string,
  approvedOnly: PropTypes.bool,
  requiredApprovalStepId: PropTypes.string,
  summarizeEmployees: PropTypes.bool,
  summarizeDays: PropTypes.bool,
  summarizeProject: PropTypes.bool,
  generateBatchNumber: PropTypes.bool,
  generateFormNumber: PropTypes.bool,
  addLineNumbers: PropTypes.bool,
  includeZeroHours: PropTypes.bool,
  access: PropTypes.shape({
    userIds: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string,
        isPermanent: PropTypes.bool,
      }),
    ),
    positionIds: PropTypes.arrayOf(PropTypes.string),
  }),
});

export const EXPORTABLE_FIELD_TYPES = new Set([
  'text',
  'calculation',
  'attribute',
  'dropdown',
  'yes-no',
  'table',
  'timeTrackingGrid',
  'dateTime',
  'dateRange',
  'gpsLocation',
  'staticText',
]);

export const TIME_TRACKING_GRID_EXPORT_COLUMNS = [
  { key: 'equipmentHours', title: 'Equipment Hours' },
  { key: 'labourRegularHours', title: 'Labour Regular Hours' },
  { key: 'otHours', title: 'OT Hours' },
  { key: 'dtHours', title: 'DT Hours' },
  { key: 'totalHours', title: 'Total Hours' },
  { key: 'userName', title: 'User' },
  { key: 'equipmentName', title: 'Equipment' },
  { key: 'project', title: 'Project' },
  { key: 'costCode', title: 'Cost Code' },
];

export const TIME_TRACKING_GRID_EXPORT_KEYS = new Set(
  TIME_TRACKING_GRID_EXPORT_COLUMNS.map(({ key }) => key),
);

export const TIME_TRACKING_GRID_HOUR_KEYS = new Set([
  'equipmentHours',
  'labourRegularHours',
  'otHours',
  'dtHours',
  'totalHours',
]);

export const MULTI_EXPORTABLE_FIELD_TYPES = new Set([
  'text',
  'staticText',
  'dropdown',
  'gpsLocation',
  'attribute',
]);

export const getExportFieldType = (field = {}) => (
  field.selectedType === 'dynamicAttribute'
    ? field.configProps?.fieldType
    : field.selectedType
);

const emptyColumn = {
  key: 'empty',
  title: 'Empty',
};

const staticTextColumn = {
  key: 'staticText',
  title: 'Static Text',
};

const exportDateColumn = {
  key: 'exportDate',
  title: 'Export Date',
};

const projectColumns = (t) => ({
  key: 'project',
  title: t('Project'),
  selectable: false,
  children: [{
    key: 'projects.name',
    title: `${t('Project')} - Name`,
  }, {
    key: 'projects.number',
    title: `${t('Project')} - Number`,
  }, {
    key: 'projects.address',
    title: `${t('Project')} - Address`,
  }, {
    key: 'projects.qboClass',
    title: `${t('Project')} - QuickBooks Class`,
  }],
});

const userColumns = {
  key: 'user',
  title: 'User',
  selectable: false,
  children: [{
    key: 'users.name',
    title: 'User - Name',
  }, {
    key: 'users.employeeId',
    title: 'User - Employee ID',
  }, {
    key: 'users.email',
    title: 'User - Email',
  }, {
    key: 'users.wage',
    title: 'User - Wage',
  }],
};

const divisionColumns = {
  key: 'division',
  title: 'Division',
  selectable: false,
  children: [{
    key: 'divisions.name',
    title: 'Division - Name',
  }, {
    key: 'divisions.code',
    title: 'Division - Code',
  }],
};

export const getTimecardFields = (t) => [
  emptyColumn,
  staticTextColumn,
  exportDateColumn,
  {
    key: 'displayId',
    title: 'ID',
  }, {
    key: 'date',
    title: 'Date',
  }, {
    key: 'startTime',
    title: 'Start Time',
  }, {
    key: 'endTime',
    title: 'End Time',
  }, {
    key: 'timezone',
    title: 'Timezone',
  }, {
    key: 'totalHours',
    title: 'Total Hours',
  }, {
    key: 'workHours',
    title: 'Work Hours',
  }, {
    key: 'breakHours',
    title: 'Break Hours',
  }, {
    key: 'regularHours',
    title: 'Regular Hours',
  }, {
    key: 'otHours',
    title: 'Overtime Hours',
  }, {
    key: 'doubleOT',
    title: 'Double Time Hours',
  }, {
    key: 'ptoHours',
    title: 'PTO Hours',
  }, {
    key: 'wageType',
    title: 'Wage Type',
  }, {
    key: 'calculatedWage',
    title: 'Calculated Wage',
  }, {
    key: 'cost',
    title: 'Calculated Cost',
  }, {
    key: 'burdenCost',
    title: 'Burden Cost',
  }, {
    key: 'type',
    title: 'Type',
  }, {
    key: 'state',
    title: 'Submitted Status',
  }, {
    key: 'note',
    title: 'Note',
  }, {
    key: 'serviceLocation',
    title: 'Service Location',
  },
  userColumns,
  divisionColumns,
  projectColumns(t),
  {
    key: 'phase',
    title: 'Phase',
    selectable: false,
    children: [{
      key: 'phases.name',
      title: 'Phase - Name',
    }, {
      key: 'phases.description',
      title: 'Phase - Description',
    }],
  }, {
    key: 'costcode',
    title: 'Cost Code',
    selectable: false,
    children: [{
      key: 'costcodes.code',
      title: 'Cost Code - Code',
    }, {
      key: 'costcodes.name',
      title: 'Cost Code - Name',
    }, {
      key: 'costcodes.description',
      title: 'Cost Code - Description',
    }, {
      key: 'costcodes.hours',
      title: 'Cost Code - Hours',
    }, {
      key: 'costcodes.hourlywage',
      title: 'Cost Code - Hourly Wage',
    }],
  }, {
    key: 'union',
    title: 'Union',
    selectable: false,
    children: [{
      key: 'union.name',
      title: 'Union - Name',
    }, {
      key: 'local.name',
      title: 'Local - Name',
    }, {
      key: 'class.name',
      title: 'Class - Name',
    }, {
      key: 'class.hourlywage',
      title: 'Class - Hourly Wage',
    }],
  }];

export const getFormFields = (t) => [
  emptyColumn,
  staticTextColumn,
  exportDateColumn,
  {
    key: 'name',
    title: 'Form Name',
  }, {
    key: 'number',
    title: 'Number',
  }, {
    key: 'createdAt',
    title: 'Created On',
  }, {
    key: 'lastUpdated',
    title: 'Last Updated',
  },
  userColumns,
  projectColumns(t),
  divisionColumns,
];

export const getBoardCardFields = () => [
  emptyColumn,
  staticTextColumn,
  {
    key: 'title',
    title: 'Card Title',
  }, {
    key: 'cardNumber',
    title: 'Card Number',
  }, {
    key: 'status',
    title: 'Status',
  }, {
    key: 'createdAt',
    title: 'Created On',
    isDateField: true,
  }, {
    key: 'lastUpdated',
    title: 'Last Updated',
    isDateField: true,
  }, {
    key: 'assignedUsers',
    title: 'Assigned Users',
  },
];

export const FIELDS_WITH_TEXT = new Set(['staticText']);
export const FIELDS_WITH_FILTERS = new Set(['costcodes.hours']);
export const FIELDS_WITH_CONFIG = new Set(['date', 'exportDate']);
const DATE_CONFIG = {
  options: [
    { label: 'mm/dd/yyyy', value: 'mm/dd/yyyy' },
    { label: 'mm/dd/yy', value: 'mm/dd/yy' },
    { label: 'dd/mm/yyyy', value: 'dd/mm/yyyy' },
    { label: 'dd/mm/yy', value: 'dd/mm/yy' },
  ],
  default: 'mm/dd/yyyy',
};
export const CONFIG_MAP = {
  date: DATE_CONFIG,
  exportDate: DATE_CONFIG,
};
